import { Calendar, ChevronLeft, ChevronRight, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useHorariosPorDia } from '../../api/hooks/useHorarios';
import { useAuthStore } from '../../store/authStore';
import type { DiaSemana } from '../../types/models';

const DIAS_MAP: DiaSemana[] = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
const DAY_NAMES = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface Props {
  aulasHoje: any[];
  statusMap: Record<string, string>;
}

export function MobileWeekCalendar({ aulasHoje, statusMap }: Props) {
  const user = useAuthStore((s) => s.user);
  const [weekOffset, setWeekOffset] = useState(0);
  const today = new Date();

  const startOfWeek = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() - today.getDay() + 1 + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const days = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    }), [startOfWeek]);

  const isToday = (d: Date) =>
    d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();

  // Selected day index (default: today if visible, else Monday)
  const todayIdx = days.findIndex(d => isToday(d));
  const [selectedIdx, setSelectedIdx] = useState(todayIdx >= 0 ? todayIdx : 0);

  // Reset selection when week changes
  const handleWeekChange = (delta: number) => {
    setWeekOffset(w => {
      const newOffset = w + delta;
      // If going back to current week, select today
      if (newOffset === 0) {
        const ti = days.findIndex(d => isToday(d));
        setSelectedIdx(ti >= 0 ? ti : 0);
      } else {
        setSelectedIdx(0);
      }
      return newOffset;
    });
  };

  const handleGoToday = () => {
    setWeekOffset(0);
    const ti = days.findIndex(d => isToday(d));
    setSelectedIdx(ti >= 0 ? ti : 0);
  };

  const selectedDate = days[selectedIdx];
  const selectedIsToday = selectedDate ? isToday(selectedDate) : false;
  const selectedDia: DiaSemana = DIAS_MAP[selectedIdx];

  // Fetch horarios for the selected day (skip if it's today - we already have aulasHoje)
  const { aulas: aulasDia, isLoading: loadingDia } = useHorariosPorDia(
    !selectedIsToday ? user?.id : undefined,
    !selectedIsToday ? selectedDia : undefined,
  );

  // Which aulas to show
  const aulasExibir = selectedIsToday ? aulasHoje : aulasDia.filter(a => a.tipo === 'AULA');

  return (
    <div className="mobile-week-cal">
      <div className="mobile-week-cal-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={14} style={{ color: '#2563eb' }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 700 }}>
            {MONTH_NAMES[startOfWeek.getMonth()]} {startOfWeek.getFullYear()}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => handleWeekChange(-1)} style={{ border: 'none', background: 'var(--bg-main, #f2f2f7)', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <ChevronLeft size={14} />
          </button>
          {weekOffset !== 0 && (
            <button onClick={handleGoToday} style={{ border: 'none', background: '#2563eb', borderRadius: 8, padding: '0 8px', height: 28, fontSize: '0.58rem', fontWeight: 700, color: 'white', cursor: 'pointer' }}>Hoje</button>
          )}
          <button onClick={() => handleWeekChange(1)} style={{ border: 'none', background: 'var(--bg-main, #f2f2f7)', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Days row - clickable */}
      <div className="mobile-week-days">
        {days.map((d, i) => {
          const it = isToday(d);
          const selected = i === selectedIdx;
          return (
            <button
              key={i}
              className={`mobile-week-day ${it ? 'today' : ''} ${selected && !it ? 'selected' : ''}`}
              onClick={() => setSelectedIdx(i)}
              style={{ border: 'none', background: selected && !it ? 'rgba(37,99,235,0.08)' : undefined }}
            >
              <span className="day-name">{DAY_NAMES[i]}</span>
              <span className="day-num">{d.getDate()}</span>
              <div className="day-dots">
                {it && aulasHoje.length > 0 && aulasHoje.slice(0, 3).map((_, j) => (
                  <span key={j} className="dot" style={{ background: it ? 'rgba(255,255,255,0.6)' : '#2563eb' }} />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Events for the selected day */}
      {loadingDia && (
        <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
        </div>
      )}

      {!loadingDia && aulasExibir.length > 0 && (
        <div className="mobile-week-events">
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', padding: '0 4px 4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            {selectedIsToday ? 'Hoje' : DAY_NAMES[selectedIdx] + ', ' + selectedDate?.getDate() + ' ' + MONTH_NAMES[selectedDate?.getMonth() ?? 0]}
            <span style={{ fontWeight: 500, marginLeft: 6, textTransform: 'none' }}>
              {aulasExibir.length} aula{aulasExibir.length > 1 ? 's' : ''}
            </span>
          </div>
          {aulasExibir.map((h, i) => {
            const st = selectedIsToday ? statusMap[h.id] : undefined;
            const done = st === 'CONCLUIDA';
            return (
              <div key={h.id || i} className="mobile-week-event" style={{
                background: done ? 'rgba(52,211,153,0.06)' : 'var(--bg-main, #f9fafb)',
                borderLeftColor: done ? '#34d399' : '#2563eb',
              }}>
                <span className="evt-time">{h.horaInicio}</span>
                <span className="evt-name">{h.materia?.nome || h.numeroAula + 'a Aula'}</span>
                {done && <CheckCircle2 size={12} style={{ color: '#34d399', flexShrink: 0 }} />}
                {h.turma?.nome && <span className="evt-badge" style={{ background: done ? 'rgba(52,211,153,0.12)' : 'rgba(37,99,235,0.08)', color: done ? '#059669' : '#2563eb' }}>{h.turma.nome}</span>}
              </div>
            );
          })}
        </div>
      )}

      {!loadingDia && aulasExibir.length === 0 && (
        <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted, #8e8e93)' }}>
          {selectedIsToday ? 'Sem aulas programadas para hoje' : `Sem aulas na ${DAY_NAMES[selectedIdx]}`}
        </div>
      )}
    </div>
  );
}
